import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ReactModal from "react-modal";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import {
  useGetSearchByEmailQuery,
  useGetLookupByEmailQuery,
  useThreadsMutation,
  useGetTemplateQuery,
} from "../../Services/email/EmailTemplate";
import EmailTemplateModal from "../../components/email/EmailTemplateModal";
import { toast } from "react-toastify";
import { Box, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CancelIcon from "@mui/icons-material/Cancel";
import LoadingSpinner from "../../components/LoadingSpinner";
import AddNewTicketContactModal from "../../components/ticket/AddNewTicketContactModal";
import axios from "axios";
import { useSignatureInEmailQuery } from "../../Services/settingsAPIs/SignatureApi";

interface IProps {
  signatureData: any;
}

const Compose : React.FC<IProps>= ({signatureData}) => {
  const [showCCField, setShowCCField] = useState(false);
  const [showBccField, setShowBccField] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState<any>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedTemplateDescription, setSelectedTemplateDescription] =
    useState<any>("");
  const [storeTemplateData, setStoreTemplateData] = useState<any>([]);
  const [editorHtml, setEditorHtml] = useState<any>("");
  const [inputValue, setInputValue] = useState("");
  const [ccInputValue, setCcInputValue] = useState("");
  const [bccInputValue, setBccInputValue] = useState("");
  const [queryParam, setQueryParam] = useState<any>();
  const [emailOptions, setEmailOptions] = useState<any[]>([]);
  const [lookupEmail, setLookupEmail] = useState("");
  const [emailNotPresent, setEmailNotPresent] = useState<any>("");
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateAttachment, setTemplateAttachment] = useState<any>();
  const [isAddNewContactModalOpen, setAddNewContactModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>();
  const [storeNewEmailId, setStoreNewEmailId] = useState("");
  const [signature, setSignature] = useState<any>({});

  const { data } = useGetSearchByEmailQuery(queryParam, {
    skip: !queryParam,
  });

  const { data: lookupEmailData } = useGetLookupByEmailQuery(lookupEmail, {
    skip: !lookupEmail,
  });

  const { data: getTemplate } = useGetTemplateQuery({
    team_id: localStorage.getItem("teamId"),
  });

  const [thredsEmail] = useThreadsMutation();



  // Ensure signatureData is always treated as an array
  const signatureArray = Array.isArray(signatureData)
    ? signatureData
    : signatureData
    ? [signatureData]
    : [];

  const nonDefaultSignatures = signatureArray.filter(
    (sig) => sig.is_default === false
  );

  useEffect(() => {
    if (nonDefaultSignatures.length > 0) {
      setSignature(nonDefaultSignatures);
      setEditorHtml(nonDefaultSignatures[0].signature_html);
    } else {
      setSignature([]);
      setEditorHtml("");
    }
  }, [signatureData]);

  const handleFileChange = async (event) => {
    const newFiles = Array.from(event.target.files);
    const filePromises = newFiles.map(
      (file: any) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const fileData = {
              file_name: file.name,
              file: e.target.result.split(",")[1], // Base64 part
            };
            resolve(fileData);
          };
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        })
    );

    try {
      const fileDataArray = await Promise.all(filePromises);
      setFiles((prevFiles) => [...prevFiles, ...fileDataArray]);
    } catch (error: any) {
      toast.error("An error occurred while uploading files. Please try again.");
    }
  };

  const openContactModal = () => setAddNewContactModalOpen(true);

  const extractEmailOptions = (data) =>
    data ? data.map((item) => ({ label: item.email })) : [];

  useEffect(() => {
    if (data) setEmailOptions(extractEmailOptions(data));
  }, [data]);

  useEffect(() => {
    if (getTemplate) setStoreTemplateData(getTemplate);
  }, [getTemplate]);

  useEffect(() => {
    if (signatureData) {
      const signatures = Array.isArray(signatureData)
        ? signatureData
        : [signatureData];
      setSignature(signatures);

      if (signatures.length > 0 && signatures[0].signature_html) {
        setEditorHtml(signatures[0].signature_html);
      }
    }
  }, [signatureData]);

  useEffect(() => {
    if (selectedTemplateDescription && selectedTemplateDescription.content) {
      setSubject(selectedTemplateDescription.subject || "");

      const signatureHtml =
        signature && signature.length > 0 ? signature[0].signature_html : "";

      // Append signature to template content
      const newContent = `${selectedTemplateDescription.content}<br/><br/>${signatureHtml}`;
      setEditorHtml(newContent);
    }
  }, [selectedTemplateDescription, signature]);

  const handleInputChange = (event, newInputValue, setInputValue) => {
    if (event?.type === "click" || event?.type === "keydown") {
      setInputValue("");
      const emailOptionExists = emailOptions.some(
        (option) => option.label === newInputValue
      );

      if (!emailOptionExists) {
        setEmailNotPresent(false);
        openContactModal();
        setStoreNewEmailId(event.target.value);
      }
    }
    if (event?.type === "change") {
      setInputValue(newInputValue);
      setQueryParam({ email: newInputValue });
    }
  };

  const handleAddEmail = (email, setEmails, emails) => {
    if (email && isValidEmail(email)) {
      if (!emails.includes(email.trim())) {
        setEmails([...emails, email.trim()]);
        setInputValue("");
      }
    }
  };

  const handleDeleteEmail = (emailToDelete, setEmails, emails) => {
    setEmails(emails.filter((email) => email !== emailToDelete));
  };

  const isValidEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const handleCopyEmail = (e: React.MouseEvent, email: string) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(email).then(
      () => toast.success("Email copied to clipboard"),
      () => toast.error("Failed to copy")
    );
  };

  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "700px",
      height: "600px",
    },
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const handleEditorChange = (content) => setEditorHtml(content);
  const toggleCCField = () => setShowCCField(!showCCField);
  const toggleBccField = () => setShowBccField(!showBccField);

  useEffect(() => {
    if (isSubmitting && lookupEmailData) {
      const queryParam = {
        contact: lookupEmailData?.id,
        original_subject: subject,
        html_content: editorHtml,
        to: emails,
        bcc: bcc,
        cc: cc,
        attachments: files,
        disposition: null,
        status: null,
        template_attachments: Array.isArray(templateAttachment)
          ? templateAttachment.map((attachment) => attachment.id)
          : [],
      };
      thredsEmail(queryParam)
        .then((response: any) => {
          if (
            response?.error?.data &&
            Object.keys(response.error.data).length > 0
          ) {
            if (response?.error?.data?.["server-configuration"]) {
              toast.error("Email is not configured");
            } else {
              const firstErrorKey = Object.keys(response.error.data)[0];
              const errorMsg = response.error.data[firstErrorKey];
              toast.error(
                `Error: ${firstErrorKey} - ${
                  typeof errorMsg === "string"
                    ? errorMsg
                    : JSON.stringify(errorMsg)
                }`
              );
            }
            setIsSubmitting(false);
            return;
          }

          toast.success("Email sent successfully!");
          setBcc([]);
          setEmails([]);
          setCc([]);
          setEditorHtml("");
          setSubject("");
          setFiles([]);
          setTemplateAttachment([]);
          setIsSubmitting(false);
        })
        .catch((error) => {
          console.error("Email send error:", error);
          toast.error(
            "An error occurred while sending the email. Please try again."
          );
          setIsSubmitting(false);
        })
        .finally(() => setIsSubmitting(false));
    }
  }, [lookupEmailData]);

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const handleSubmit = async () => {
    if (subject.trim().length === 0) {
      return toast.error("Subject cannot be empty.");
    }
    if (!editorHtml || editorHtml.trim().length === 0) {
      return toast.error("Email content cannot be empty.");
    }
    if (emails.length === 0) {
      return toast.error("Please add at least one recipient.");
    }

    const allEmails = [...emails, ...cc, ...bcc].filter(
      (v, i, arr) => arr.indexOf(v) === i
    );

    setIsSubmitting(true);

    for (const email of allEmails) {
      try {
        const res = await axios.get(
          `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
            },
            params: { email },
          }
        );
        if (!res.data || res.data.length === 0) {
          setEmailNotPresent(email);
          setStoreNewEmailId(email);
          setAddNewContactModalOpen(true);
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        toast.error("Failed to fetch customer email suggestions.");
        setIsSubmitting(false);
        return;
      }
    }

    // All emails are present
    setLookupEmail(emails[emails.length - 1]);
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "align",
    "script",
    "background",
    "code-block",
  ];

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }, { font: [] }],
      ["bold", "italic", "underline", "strike"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "blockquote", "code-block"],
      [{ color: [] }, { background: [] }, { align: [] }],
      ["clean"],
    ],
  };

  const handleDeleteFile = (fileToDelete) => () => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToDelete));
  };

  const handleDeleteAttachmentFile = (fileToDeleteId) => async () => {
    setTemplateAttachment((prev) =>
      prev.filter((file) => String(file.id) !== String(fileToDeleteId))
    );
  };

  const handleDownloadFile = (fileId) => window.open(`${fileId}`, "_blank");

  return (
    <div id="kt_app_content_container" className="app-container">
      <div className="d-flex flex-column flex-lg-row">
        <div className="flex-lg-row-fluid ms-lg-7 ms-xl-10">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between py-3">
              <h2 className="card-title m-0">New Message</h2>
            </div>
            <div className="card-body p-0">
              {isSubmitting ? (
                <LoadingSpinner />
              ) : (
                <div id="kt_inbox_compose_form">
                  <div className="d-block">
                    <div className="d-flex align-items-center border-bottom px-8 min-h-50px">
                      <div className="text-dark fw-bold w-50px">To:</div>
                      <div style={{ width: "100%" }}>
                        <Autocomplete
                          freeSolo
                          options={emailOptions}
                          getOptionLabel={(option: any) => option.label}
                          onChange={(event, value) => {
                            if (value && isValidEmail(value.label)) {
                              handleAddEmail(value.label, setEmails, emails);
                              setInputValue(""); // Clear the input
                            }
                          }}
                          inputValue={inputValue}
                          onInputChange={(event, newInputValue) =>
                            handleInputChange(
                              event,
                              newInputValue,
                              setInputValue
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{ border: "none" }} // Remove border
                              fullWidth
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleAddEmail(inputValue, setEmails, emails);
                                  setInputValue(""); // Clear the input
                                  e.preventDefault(); // Prevent form submission or default behavior
                                }
                              }}
                              onBlur={async () => {
                                if (inputValue.trim() !== "") {
                                  try {
                                    const res = await axios.get(
                                      `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
                                      {
                                        headers: {
                                          Authorization: `Bearer ${localStorage.getItem(
                                            "auth-token"
                                          )}`,
                                        },
                                        params: { email: inputValue.trim() },
                                      }
                                    );
                                    if (!res.data || res.data.length === 0) {
                                      setEmailNotPresent(inputValue.trim());
                                      setStoreNewEmailId(inputValue.trim());
                                      setAddNewContactModalOpen(true);
                                      setIsSubmitting(false);
                                      handleAddEmail(
                                        inputValue,
                                        setEmails,
                                        emails
                                      );
                                      setInputValue("");
                                      return;
                                    } else {
                                      handleAddEmail(
                                        inputValue,
                                        setEmails,
                                        emails
                                      );
                                      setInputValue("");
                                    }
                                  } catch (error) {
                                    toast.error(
                                      "Failed to fetch customer email suggestions."
                                    );
                                    setIsSubmitting(false);
                                  }
                                }
                              }}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                  >
                                    {emails.map((email, index) => (
                                      <Chip
                                        key={index}
                                        label={email}
                                        size="small"
                                        sx={{
                                          minHeight: 34,
                                          px: 1.25,
                                          "& .MuiChip-deleteIcon": { color: "inherit" },
                                          "& .MuiChip-deleteIcon .chip-delete-x-only": { color: "#64748b" },
                                          "& .MuiChip-deleteIcon .chip-delete-x-only:hover": { color: "#ef4444" },
                                        }}
                                        onDelete={() =>
                                          handleDeleteEmail(
                                            email,
                                            setEmails,
                                            emails
                                          )
                                        }
                                        deleteIcon={
                                          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                            <span className="chip-delete-x-only" style={{ display: "inline-flex", cursor: "pointer" }}>
                                              <CancelIcon sx={{ fontSize: 16 }} />
                                            </span>
                                            <IconButton
                                              size="small"
                                              sx={{ p: 0.2 }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopyEmail(e, email);
                                              }}
                                              title="Copy email"
                                            >
                                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                          </span>
                                        }
                                        className="mat-chip-custom"
                                        style={{ marginLeft: 5 }}
                                      />
                                    ))}
                                  </Stack>
                                ),
                              }}
                            />
                          )}
                        />
                      </div>
                      <div className="w-75px text-end">
                        <span
                          className="text-muted fs-bold cursor-pointer text-hover-primary me-2"
                          onClick={toggleCCField}
                        >
                          Cc
                        </span>
                        <span
                          className="text-muted fs-bold cursor-pointer text-hover-primary"
                          onClick={toggleBccField}
                        >
                          Bcc
                        </span>
                      </div>
                    </div>
                    {showCCField && (
                      <div className="d-flex align-items-center border-bottom px-8 min-h-50px">
                        <div className="text-dark fw-bold w-50px">Cc:</div>
                        <div style={{ width: "100%" }}>
                          <Autocomplete
                            freeSolo
                            options={emailOptions}
                            getOptionLabel={(option: any) => option.label}
                            onChange={(event, value) => {
                              if (value && isValidEmail(value.label)) {
                                handleAddEmail(value.label, setCc, cc);
                                setCcInputValue(""); // Clear the input
                              }
                            }}
                            inputValue={ccInputValue}
                            onInputChange={(event, newInputValue) =>
                              handleInputChange(
                                event,
                                newInputValue,
                                setCcInputValue
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                fullWidth
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleAddEmail(ccInputValue, setCc, cc);
                                    setCcInputValue(""); // Clear the input
                                    e.preventDefault(); // Prevent form submission or default behavior
                                  }
                                }}
                                onBlur={async () => {
                                  if (ccInputValue.trim() !== "") {
                                    try {
                                      const res = await axios.get(
                                        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
                                        {
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem(
                                              "auth-token"
                                            )}`,
                                          },
                                          params: {
                                            email: ccInputValue.trim(),
                                          },
                                        }
                                      );
                                      if (!res.data || res.data.length === 0) {
                                        setEmailNotPresent(ccInputValue.trim());
                                        setStoreNewEmailId(ccInputValue.trim());
                                        setAddNewContactModalOpen(true);
                                        setIsSubmitting(false);
                                        setCcInputValue("");
                                        handleAddEmail(ccInputValue, setCc, cc);
                                        setCcInputValue("");
                                        return;
                                      } else {
                                        handleAddEmail(ccInputValue, setCc, cc);
                                        setCcInputValue("");
                                      }
                                    } catch (error) {
                                      toast.error(
                                        "Failed to fetch customer email suggestions."
                                      );
                                      setIsSubmitting(false);
                                    }
                                  }
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                    >
                                      {cc.map((email, index) => (
                                        <Chip
                                          key={index}
                                          label={email}
                                          size="small"
                                          sx={{
                                            minHeight: 34,
                                            px: 1.25,
                                            "& .MuiChip-deleteIcon": { color: "inherit" },
                                            "& .MuiChip-deleteIcon .chip-delete-x-only": { color: "#64748b" },
                                            "& .MuiChip-deleteIcon .chip-delete-x-only:hover": { color: "#ef4444" },
                                          }}
                                          onDelete={() =>
                                            handleDeleteEmail(email, setCc, cc)
                                          }
                                          deleteIcon={
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                              <span className="chip-delete-x-only" style={{ display: "inline-flex", cursor: "pointer" }}>
                                                <CancelIcon sx={{ fontSize: 16 }} />
                                              </span>
                                              <IconButton
                                                size="small"
                                                sx={{ p: 0.2 }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCopyEmail(e, email);
                                                }}
                                                title="Copy email"
                                              >
                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </span>
                                          }
                                          className="mat-chip-custom"
                                          style={{ marginLeft: 5 }}
                                        />
                                      ))}
                                    </Stack>
                                  ),
                                }}
                              />
                            )}
                          />
                        </div>
                        <div className="ms-auto w-75px text-end">
                          <span
                            className="fs-bold cursor-pointer"
                            onClick={toggleCCField}
                          >
                            Hide Cc
                          </span>
                        </div>
                      </div>
                    )}
                    {showBccField && (
                      <div className="d-flex align-items-center border-bottom px-8 min-h-50px">
                        <div className="text-dark fw-bold w-50px">Bcc:</div>
                        <div style={{ width: "100%" }}>
                          <Autocomplete
                            freeSolo
                            options={emailOptions}
                            getOptionLabel={(option: any) => option.label}
                            onChange={(event, value) => {
                              if (value && isValidEmail(value.label)) {
                                handleAddEmail(value.label, setBcc, bcc);
                                setBccInputValue(""); // Clear the input
                              }
                            }}
                            inputValue={bccInputValue}
                            onInputChange={(event, newInputValue) =>
                              handleInputChange(
                                event,
                                newInputValue,
                                setBccInputValue
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                fullWidth
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleAddEmail(bccInputValue, setBcc, bcc);
                                    setBccInputValue(""); // Clear the input
                                    e.preventDefault(); // Prevent form submission or default behavior
                                  }
                                }}
                                onBlur={async () => {
                                  if (bccInputValue.trim() !== "") {
                                    try {
                                      const res = await axios.get(
                                        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
                                        {
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem(
                                              "auth-token"
                                            )}`,
                                          },
                                          params: {
                                            email: bccInputValue.trim(),
                                          },
                                        }
                                      );
                                      if (!res.data || res.data.length === 0) {
                                        setEmailNotPresent(
                                          bccInputValue.trim()
                                        );
                                        setStoreNewEmailId(
                                          bccInputValue.trim()
                                        );
                                        setAddNewContactModalOpen(true);
                                        setIsSubmitting(false);
                                        handleAddEmail(
                                          bccInputValue,
                                          setBcc,
                                          bcc
                                        );
                                        setBccInputValue("");
                                        return;
                                      } else {
                                        handleAddEmail(
                                          bccInputValue,
                                          setBcc,
                                          bcc
                                        );
                                        setBccInputValue("");
                                      }
                                    } catch (error) {
                                      toast.error(
                                        "Failed to fetch customer email suggestions."
                                      );
                                      setIsSubmitting(false);
                                    }
                                  }
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                    >
                                      {bcc.map((email, index) => (
                                        <Chip
                                          key={index}
                                          label={email}
                                          size="small"
                                          sx={{
                                            minHeight: 34,
                                            px: 1.25,
                                            "& .MuiChip-deleteIcon": { color: "inherit" },
                                            "& .MuiChip-deleteIcon .chip-delete-x-only": { color: "#64748b" },
                                            "& .MuiChip-deleteIcon .chip-delete-x-only:hover": { color: "#ef4444" },
                                          }}
                                          onDelete={() =>
                                            handleDeleteEmail(
                                              email,
                                              setBcc,
                                              bcc
                                            )
                                          }
                                          deleteIcon={
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                              <span className="chip-delete-x-only" style={{ display: "inline-flex", cursor: "pointer" }}>
                                                <CancelIcon sx={{ fontSize: 16 }} />
                                              </span>
                                              <IconButton
                                                size="small"
                                                sx={{ p: 0.2 }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCopyEmail(e, email);
                                                }}
                                                title="Copy email"
                                              >
                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </span>
                                          }
                                          className="mat-chip-custom"
                                          style={{ marginLeft: 5 }}
                                        />
                                      ))}
                                    </Stack>
                                  ),
                                }}
                              />
                            )}
                          />
                        </div>
                        <div className="ms-auto w-75px text-end">
                          <span
                            className="fs-bold cursor-pointer"
                            onClick={toggleBccField}
                          >
                            Hide Bcc
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* begin::Subject */}
                  <div className="border-bottom">
                    <input
                      className="form-control form-control-transparent border-0 px-8 min-h-45px"
                      name="compose_subject"
                      placeholder="Subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>
                  {/* end::Subject */}
                  {/* begin::Message */}
                  <div className="card-body p-5">
                    <ReactQuill
                      theme="snow"
                      value={editorHtml}
                      onChange={handleEditorChange}
                      modules={modules}
                      formats={formats}
                      style={{ height: "200px" }}
                    />
                  </div>
                  <div className="mt-5 p-5">
                    {files.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        {files.map((file, index) => (
                          <Chip
                            key={index}
                            label={file.file_name}
                            onDelete={handleDeleteFile(file)}
                          />
                        ))}
                      </Box>
                    )}

                    {templateAttachment?.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        {templateAttachment?.map((file, index) => (
                          <Chip
                            key={index}
                            label={file.file_name}
                            onClick={() => handleDownloadFile(file.file)}
                            onDelete={handleDeleteAttachmentFile(file.id)}
                            sx={{ cursor: "pointer" }} // Adds a pointer cursor on hover
                          />
                        ))}
                      </Box>
                    )}
                  </div>
                  {/* end::Message */}
                  {/* begin::Footer */}
                  <div className="d-flex flex-stack flex-wrap gap-2 py-5 ps-8 pe-5 border-top mt-5">
                    <div className="d-flex align-items-center me-3">
                      <div className="btn-group me-4">
                        <button
                          className="btn btn-primary fs-bold px-6"
                          data-kt-inbox-form="send"
                          onClick={handleSubmit}
                        >
                          <span className="indicator-label">Send</span>
                          <span className="indicator-progress">
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2" />
                          </span>
                        </button>
                      </div>
                      <label
                        htmlFor="attachFileInput"
                        className="btn btn-active-light-primary btn-icon"
                        title="You can add attachments for the following 
                                        file types: xls,pdf,csv,doc,jpg,png upto maximum size of 15MB
            "
                      >
                        <i className="bi bi-paperclip fs-2"></i>
                        <input
                          type="file"
                          id="attachFileInput"
                          className="d-none"
                          multiple
                          onChange={handleFileChange}
                        />
                      </label>
                      <div
                        className="btn btn-active-light-primary btn-icon fs-bold px-6"
                        onClick={openModal}
                      >
                        <label title="Template">
                          <i className="fas fa-mail-bulk fs-3"></i>
                        </label>
                      </div>
                      <ReactModal
                        isOpen={isModalOpen}
                        onRequestClose={closeModal}
                        style={customModalStyles}
                        contentLabel="Email templates"
                        shouldCloseOnOverlayClick={false}
                      >
                        <EmailTemplateModal
                          closeModal={closeModal}
                          setSelectedTemplateDescription={
                            setSelectedTemplateDescription
                          }
                          storeTemplateData={storeTemplateData}
                          setTemplateAttachment={setTemplateAttachment}
                        />
                      </ReactModal>

                      <ReactModal
                        isOpen={isAddNewContactModalOpen}
                        onRequestClose={() => setAddNewContactModalOpen(false)}
                        style={customModalStyles}
                        contentLabel="New Contact"
                        shouldCloseOnOverlayClick={false}
                      >
                        <AddNewTicketContactModal
                          closeModal={() => setAddNewContactModalOpen(false)}
                          emailNotPresent={emailNotPresent}
                          setSelectedContact={setSelectedContact}
                          storeNewEmailId={storeNewEmailId}
                          setEmailNotPresent={setEmailNotPresent}
                        />
                      </ReactModal>
                    </div>
                  </div>
                  {/* end::Footer */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compose;
